export interface AppVersionPayload {
  buildId: string;
  /** 语义版本，可与 buildId 独立变化（如紧急修复同号不同构建） */
  version?: string;
  /** 为 true 时客户端不可「稍后更新」，必须点立即更新 */
  requiresHardRefresh: boolean;
  /** 本次发布的更新内容列表，由构建时 VITE_APP_CHANGELOG 注入 */
  changelog?: string[];
}

export function parseVersionPayload(json: unknown): AppVersionPayload | null {
  if (json === null || typeof json !== 'object') return null;
  const buildId = (json as { buildId?: unknown }).buildId;
  if (typeof buildId !== 'string' || buildId.length === 0) return null;

  const versionRaw = (json as { version?: unknown }).version;
  const version =
    typeof versionRaw === 'string' && versionRaw.trim().length > 0 ? versionRaw.trim() : undefined;

  const rh = (json as { requiresHardRefresh?: unknown }).requiresHardRefresh;
  const requiresHardRefresh = rh === true;

  const changelogRaw = (json as { changelog?: unknown }).changelog;
  const changelog = Array.isArray(changelogRaw)
    ? changelogRaw.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0
      )
    : undefined;

  return { buildId, version, requiresHardRefresh, changelog };
}

/** 记录「已完成缓存清理」的构建号；仅在执行过清理流程后写入 */
export const APP_UPDATE_APPLIED_BUILD_ID_KEY = 'jointo:app-applied-build-id';

export function getLocalBuildId(): string {
  return import.meta.env.VITE_APP_BUILD_ID;
}

export function getAppliedBuildId(): string | null {
  try {
    return window.localStorage.getItem(APP_UPDATE_APPLIED_BUILD_ID_KEY);
  } catch {
    return null;
  }
}

export function setAppliedBuildId(buildId: string): void {
  try {
    window.localStorage.setItem(APP_UPDATE_APPLIED_BUILD_ID_KEY, buildId);
  } catch {
    // ignore
  }
}

export function getLocalAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION;
}

function versionJsonUrl(): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return `${base}/version.json`;
}

export async function fetchRemoteVersionInfo(
  signal?: AbortSignal,
  onServiceUnavailable?: () => void | Promise<void>
): Promise<AppVersionPayload | null> {
  const url = versionJsonUrl();
  try {
    const res = await fetch(url, {
      cache: 'no-store',
      signal,
      headers: { Accept: 'application/json' },
    });
    if (res.status === 503) {
      await onServiceUnavailable?.();
      return null;
    }
    if (!res.ok) return null;
    const data: unknown = await res.json();
    return parseVersionPayload(data);
  } catch {
    return null;
  }
}

export function isNewerBuildAvailable(remoteBuildId: string | null, localBuildId: string): boolean {
  if (remoteBuildId === null) return false;
  return remoteBuildId !== localBuildId;
}

export interface AppUpdateCleanupInput {
  remoteBuildId: string | null;
  localBuildId: string;
  appliedBuildId: string | null;
  requiresHardRefresh: boolean;
}

/**
 * 代码已经是最新构建，但强制更新要求的本地缓存清理尚未执行。
 *
 * 浏览器刷新键不经过页面 JS，Modal 拦不住手动刷新；此时用户拿到了新代码却留着旧缓存，
 * 需要继续弹窗要求走「立即更新」完成清理。无清理记录（首次访问）视为已对齐，不打扰。
 */
export function isHardRefreshCleanupPending({
  remoteBuildId,
  localBuildId,
  appliedBuildId,
  requiresHardRefresh,
}: AppUpdateCleanupInput): boolean {
  if (remoteBuildId === null) return false;
  if (remoteBuildId !== localBuildId) return false;
  if (!requiresHardRefresh) return false;
  if (appliedBuildId === null) return false;
  return appliedBuildId !== localBuildId;
}

/** 代码已最新且无待办清理时，把清理记录推进到当前构建 */
export function shouldAlignAppliedBuildId(input: AppUpdateCleanupInput): boolean {
  if (input.remoteBuildId === null) return false;
  if (input.remoteBuildId !== input.localBuildId) return false;
  if (isHardRefreshCleanupPending(input)) return false;
  return input.appliedBuildId !== input.localBuildId;
}
